import { useEffect, useRef, useState } from "react";
import { Languages, Loader2, Scissors, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useWorkspace } from "../../store/WorkspaceContext";
import { selectMachinePatch } from "../../engine/layout";
import { sendViaTransport, testSquareCommands } from "../../lib/transport";

// Tried in order: nearly every USB vinyl cutter speaks HP-GL; DMPL = Summa/Ioline/clones whose panel is set to DMPL; GP-GL = Graphtec.
const STEPS = [
  { fmt: "hpgl", label: "HP-GL" },
  { fmt: "dmpl", label: "DMPL" },
  { fmt: "gpgl", label: "GP-GL (Graphtec)" },
];

// One-tap plotter language check: cuts a 2" square in each language in turn; the first one that comes out clean is locked in
// as the plotter's profile — nobody has to know what HP-GL or DMPL means.
export const LanguageCheck = ({ connection, disabled, disabledReason, autoStart = null, onDone }) => {
  const { state, dispatch } = useWorkspace();
  const [step, setStep] = useState(-1);  // -1 closed · 0..2 cutting/asking · 3 nothing worked
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [intro, setIntro] = useState(false);  // opened by itself after a plotter was selected for the first time
  const started = useRef(null);
  useEffect(() => { if (autoStart && started.current !== autoStart && !disabled) { started.current = autoStart; setIntro(true); } }, [autoStart, disabled]);
  const finish = (fmt) => { setStep(-1); setAsking(false); setIntro(false); onDone?.(fmt); };
  const generic = (fmt) => state.machines.find((x) => x.brand === "Generic" && x.command_format === fmt);

  const cutStep = async (i) => {
    const s = STEPS[i];
    if (!s) return setStep(3);
    if (!["bridge", "webserial", "webusb"].includes(connection?.transport)) return toast.error("Select your plotter in the list above (press Select) before running the language check");
    setStep(i); setBusy(true); setAsking(false);
    try {
      await sendViaTransport(testSquareCommands(generic(s.fmt) || s.fmt, 2), connection);
      setAsking(true);
    } catch (e) { toast.error(e.message); setStep(-1); }
    finally { setBusy(false); }
  };

  const confirm = (i) => {
    const s = STEPS[i];
    const keep = state.machine?.command_format === s.fmt ? state.machine : generic(s.fmt);
    if (!keep) return toast.error(`No ${s.label} profile available`);
    dispatch({ type: "SET", patch: { ...selectMachinePatch(keep), machinePinned: true, machineSource: null } });
    toast.success(`Your plotter speaks ${s.label} — ${keep.name} is now your plotter profile`);
    finish(s.fmt);
  };

  if (intro && step === -1) {
    return (
      <div className="space-y-2 rounded-md border border-[#2f80ff]/40 bg-[#2f80ff]/5 px-3 py-2.5 text-[12px]" data-testid="language-check-intro">
        <div className="font-medium text-[#e8e9ec]">New plotter — let's make sure it understands Xpattern before you cut a pattern.</div>
        <div className="text-[#c7cbd1]">Load a scrap of film. We'll cut a 2-inch square; if it comes out clean you're done, otherwise we try the next plotter language. Takes about a minute.</div>
        <div className="flex gap-2">
          <button data-testid="language-check-intro-start" className="btn-red h-8 px-3" onClick={() => { setIntro(false); cutStep(0); }}><Scissors className="h-3.5 w-3.5" /> Cut the test square</button>
          <button data-testid="language-check-intro-skip" className="btn h-8 px-3" onClick={() => finish("skipped")}>Skip — it already cuts fine</button>
        </div>
      </div>
    );
  }
  if (step === -1) {
    return (
      <button data-testid="language-check-btn" className="btn w-full" disabled={disabled} onClick={() => cutStep(0)}
        title={disabledReason || "Cuts a small square in each plotter language until one comes out clean — fixes a cutter that only scores a straight line"}>
        <Languages className="h-4 w-4 text-[#2f80ff]" /> Find my plotter's language
      </button>
    );
  }
  const s = STEPS[step];
  return (
    <div className="space-y-2 rounded-md border border-[#2f80ff]/40 bg-[#2f80ff]/5 px-3 py-2.5 text-[12px]" data-testid="language-check-panel">
      {step === 3 ? (
        <>
          <div className="font-medium text-[#f5a524]" data-testid="language-check-none">No language produced a square.</div>
          <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-[#c7cbd1]">
            <li>Make sure the plotter is <b>online</b> on its own panel (not paused / offline) and the blade holder is down.</li>
            <li>If the plotter has a language setting on its panel, set it to <b>HPGL</b>, then run this check again.</li>
            <li>Check the plotter notes above for a missing USB driver, or try the <b>Serial</b> option with the plotter's COM port.</li>
          </ul>
          <button data-testid="language-check-close" className="btn h-7 px-3 text-[11px]" onClick={() => finish("skipped")}>Close</button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-[#2f80ff] px-1.5 text-[10px] font-semibold text-white">Step {step + 1} of {STEPS.length}</span>
            <span className="font-medium" data-testid="language-check-step">{busy ? `Cutting a 2-inch ${s.label} square…` : asking ? `${s.label} square sent` : s.label}</span>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#2f80ff]" />}
          </div>
          {asking && (
            <>
              <div className="text-[#c7cbd1]">Look at the film: did a clean 2-inch square come out?</div>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="language-check-yes" className="btn-red h-8" onClick={() => confirm(step)}><Check className="h-3.5 w-3.5" /> Yes, a clean square</button>
                <button data-testid="language-check-no" className="btn h-8" onClick={() => cutStep(step + 1)}>{step + 1 < STEPS.length ? <><Scissors className="h-3.5 w-3.5" /> No — try {STEPS[step + 1].label}</> : <><X className="h-3.5 w-3.5" /> No — nothing cut right</>}</button>
              </div>
              <div className="text-[10px] text-[#6b7079]">A straight line, scribbles or no movement at all = No. Move the blade to a fresh spot before the next try if you like.</div>
            </>
          )}
          {!busy && <button data-testid="language-check-cancel" className="text-[11px] text-[#9aa0a8] hover:underline" onClick={() => finish("skipped")}>Cancel</button>}
        </>
      )}
    </div>
  );
};
